import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const ServiceList = ({ onEdit, readOnly = false }) => {
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { tenant_id, loadingUser } = useContext(AuthContext);

  // Format rate - preserves up to 3 decimals when needed (e.g. $0.815)
  const formatRate = (amount) => {
    const num = parseFloat(amount) || 0;
    const decimalPart = num.toString().split(".")[1] || "";
    const decimals = Math.min(Math.max(decimalPart.length, 2), 3);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Load customers
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers", err));
  }, [tenant_id, loadingUser]);

  // Load services
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    setLoading(true);
    const params = { tenant_id };
    if (selectedCustomer) {
      params.customer_id = selectedCustomer;
    }

    secureAxios
      .get("/api/services", { params })
      .then((res) => {
        setServices(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading services:", err);
        setLoading(false);
      });
  }, [tenant_id, selectedCustomer, loadingUser]);

  // Filter services by search term
  const filteredServices = services.filter(
    (service) =>
      service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (serviceId, serviceName) => {
    if (!window.confirm(`Are you sure you want to delete "${serviceName}"?`))
      return;

    try {
      await secureAxios.delete(`/api/services/${serviceId}`, {
        data: { tenant_id },
      });
      setServices(services.filter((s) => s.service_id !== serviceId));
      alert("Success! Service deleted successfully");
    } catch (err) {
      console.error("Error deleting service:", err);
      if (err.response?.status === 409) {
        alert(
          "Cannot delete service - it may be in use by authorizations or other records"
        );
      } else {
        alert("Error deleting service");
      }
    }
  };

  if (loadingUser || loading) return <p>Loading services...</p>;

  return (
    <div>
      <h2>All Services</h2>

      {/* Filters Row */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Filter by Customer:</label>
          <select
            className="form-select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Search:</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by service or customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredServices.length === 0 ? (
        <p className="text-muted">No services found.</p>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Description</th>
                  <th>Rate</th>
                  <th>Type</th>
                  <th>Customer</th>
                  {!readOnly && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((s) => (
                  <tr key={s.service_id}>
                    <td>{s.service_name}</td>
                    <td>{s.description}</td>
                    <td>
                      {formatRate(s.rate)}
                    </td>
                    <td>
                      {(s.service_type || "service") === "mileage" ? "Mileage" : "Service"}
                      {s.flat_rate && (
                        <>
                          <br />
                          <small className="text-muted">Flat Rate</small>
                        </>
                      )}
                    </td>
                    <td>{s.customer_name}</td>
                    {!readOnly && (
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => onEdit(s)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              handleDelete(s.service_id, s.service_name)
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile-Friendly Card View */}
          <div className="d-block d-md-none">
            {filteredServices.map((s) => (
              <div key={s.service_id} className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title">{s.service_name}</h5>
                  <p className="card-text">
                    <strong>Description:</strong> {s.description}
                    <br />
                    <strong>Rate:</strong> {formatRate(s.rate)}
                    <br />
                    <strong>Type:</strong>{" "}
                    {(s.service_type || "service") === "mileage" ? "Mileage" : "Service"}
                    {s.flat_rate && " (Flat Rate)"}
                    <br />
                    <strong>Customer:</strong> {s.customer_name}
                  </p>
                  {!readOnly && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          handleDelete(s.service_id, s.service_name)
                        }
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceList;